import { useId, useRef, useState } from 'react'
import * as Dialog from '@radix-ui/react-dialog'

import { Button } from '../../radix'
import { ListNameField } from './ListNameField'

interface ListNameDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  submitLabel: string
  initialName?: string
  /** Resolves to an error to show, or null once the name has been saved. */
  onSubmit: (name: string) => Promise<string | null>
}

/**
 * Create or rename a list (DEV-51010). Validation errors show inline and keep
 * the dialog open. Radix traps focus, closes on Escape and returns focus to
 * the opener; the form unmounts on close, so each open starts fresh.
 */
export function ListNameDialog({ open, onOpenChange, ...formProps }: ListNameDialogProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="animate-fade-in fixed inset-0 z-50 bg-black/60 backdrop-blur-sm" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed left-1/2 top-1/2 z-[51] max-h-[85vh] w-[calc(100vw-24px)] max-w-md -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-gray-200 bg-white p-5 shadow-xl focus:outline-none sm:p-6"
        >
          <ListNameForm {...formProps} onDone={() => onOpenChange(false)} />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function ListNameForm({
  title,
  submitLabel,
  initialName = '',
  onSubmit,
  onDone,
}: Omit<ListNameDialogProps, 'open' | 'onOpenChange'> & { onDone: () => void }) {
  const id = useId()
  const [name, setName] = useState(initialName)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (saving) return
    setSaving(true)
    const result = await onSubmit(name)
    setSaving(false)
    if (!result) {
      onDone()
      return
    }
    setError(result)
    inputRef.current?.focus()
  }

  return (
    <form noValidate onSubmit={handleSubmit}>
      <Dialog.Title className="mb-4 break-words pr-8 text-xl font-bold text-gray-900">
        {title}
      </Dialog.Title>
      <ListNameField
        ref={inputRef}
        id={id}
        value={name}
        onChange={(value) => {
          setName(value)
          setError(null)
        }}
        error={error}
      />
      <div className="mt-5 flex flex-col gap-3 sm:flex-row">
        <Button type="submit" className="sm:flex-1" disabled={saving}>
          {submitLabel}
        </Button>
        <Button type="button" variant="neutral" className="sm:flex-1" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
